import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X, Plus, Loader2 } from "lucide-react";
import Battlefield3D from "@/components/battlefield/Battlefield3D";
import CoinPicker from "@/components/battlefield/CoinPicker";
import { BinanceStream } from "@/lib/binance";
import { pairApi } from "@/lib/pairApi";
import { useIsMobile } from "@/hooks/useIsMobile";

const DEFAULT_DESKTOP = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "DOGEUSDT"];
const DEFAULT_MOBILE = ["BTCUSDT", "ETHUSDT"];
const LS_KEY = "cb_warroom_pairs";

const parseLevels = (raw) =>
  (raw || []).map((lvl) => ({ price: parseFloat(lvl[0]), qty: parseFloat(lvl[1]) }));
function midFromBook(bids, asks) {
  const bb = bids?.[0]?.price;
  const ba = asks?.[0]?.price;
  if (bb == null || ba == null) return null;
  return (bb + ba) / 2;
}
const fmtPrice = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const d = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};

export default function WarRoomPage() {
  const isMobile = useIsMobile();
  const [coins, setCoins] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [symbolSet, setSymbolSet] = useState(new Set());
  const [pairs, setPairs] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((s) => typeof s === "string")) return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_DESKTOP;
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(pairs));
    } catch {
      /* ignore */
    }
  }, [pairs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, s] = await Promise.all([pairApi.coins(), pairApi.symbols()]);
        if (cancelled) return;
        setCoins(c.coins || []);
        setSymbolSet(new Set((s.symbols || []).map((x) => x.symbol)));
      } finally {
        if (!cancelled) setCoinsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const cap = isMobile ? 2 : 4;
  const visible = pairs.slice(0, cap);

  const addPair = useCallback(
    (base, quoteHint = "USDT") => {
      const trySymbol = (b, q) => `${b}${q}`.toUpperCase();
      let symbol = trySymbol(base, quoteHint);
      if (!symbolSet.has(symbol)) {
        const alt = ["USDT", "BUSD", "USDC", "FDUSD", "BTC"];
        for (const q of alt) {
          const s = trySymbol(base, q);
          if (symbolSet.has(s)) { symbol = s; break; }
        }
      }
      if (!symbolSet.has(symbol)) return;
      if (pairs.includes(symbol)) return;
      const next = [...pairs, symbol].slice(0, cap);
      setPairs(next);
    },
    [pairs, symbolSet, cap],
  );

  const removePair = useCallback(
    (symbol) => {
      setPairs((prev) => prev.filter((s) => s !== symbol));
    },
    [],
  );

  const gridClass = isMobile
    ? "grid grid-cols-1 gap-3 p-3"
    : "grid grid-cols-2 gap-4 p-4";

  return (
    <div
      className="relative min-h-screen w-full cb-page-bg text-white overflow-x-hidden"
      data-testid="warroom-page"
    >
      <div className="absolute inset-0 cb-grid-overlay pointer-events-none" />
      <div className="absolute inset-0 cb-vignette pointer-events-none" />

      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#010308]/70 border-b border-white/10">
        <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/"
              className="cb-hud-chip flex items-center gap-2 px-3 py-1.5 hover:bg-white/10 transition-colors"
              data-testid="warroom-back"
              aria-label="Back to single view"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em]">Back</span>
            </Link>
            <div className="cb-hud-chip px-4 py-1.5 flex items-center gap-2">
              <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-400">Mode</span>
              <span
                className="font-display text-base font-bold tracking-tighter text-white"
                data-testid="warroom-title"
              >
                War Room
              </span>
              <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-500">
                {visible.length}/{cap}
              </span>
            </div>
          </div>
          {visible.length < cap && (
            <AddPairControl
              coins={coins}
              disabled={coinsLoading}
              existing={pairs}
              onAdd={addPair}
            />
          )}
        </div>
      </header>

      <div className={gridClass} data-testid="warroom-grid">
        {visible.length === 0 && (
          <div
            className="col-span-full flex flex-col items-center justify-center py-24 gap-3"
            data-testid="warroom-empty"
          >
            <p className="font-mono-tac text-xs uppercase tracking-[0.28em] text-slate-400">
              No pairs — add one to start
            </p>
          </div>
        )}
        {visible.map((symbol) => (
          <WarRoomTile
            key={symbol}
            symbol={symbol}
            symbolSet={symbolSet}
            onRemove={() => removePair(symbol)}
          />
        ))}
      </div>

      <footer
        className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-500 text-center py-6"
        data-testid="warroom-footer"
      >
        Real-time · Binance public data
      </footer>
    </div>
  );
}

function AddPairControl({ coins, disabled, existing, onAdd }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // Use CoinPicker as a self-contained trigger: hide its default trigger visuals
  // by wrapping our own button and forwarding the value change to onAdd.
  return (
    <div className="relative">
      <button
        type="button"
        className="cb-hud-chip flex items-center gap-2 px-3 py-1.5 hover:bg-white/10 transition-colors disabled:opacity-50"
        onClick={() => setPickerOpen(true)}
        disabled={disabled}
        data-testid="warroom-add-trigger"
      >
        <Plus className="w-3.5 h-3.5" />
        <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em]">Add pair</span>
      </button>
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center pt-24 px-4"
          onClick={() => setPickerOpen(false)}
          data-testid="warroom-add-scrim"
        >
          <div
            className="cb-hud-chip w-full max-w-md p-4 flex flex-col gap-3 !bg-[#010308]/95"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-300">
              Add a coin (will pair vs USDT if available)
            </span>
            <CoinPicker
              coins={coins.filter((c) => !existing.some((s) => s.startsWith(c.symbol)))}
              value=""
              disabledSymbol=""
              onChange={(base) => { onAdd(base); setPickerOpen(false); }}
              side="left"
              loading={disabled}
              testIdPrefix="warroom-add-coin"
            />
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="cb-hud-chip flex items-center justify-center gap-2 px-3 py-1.5 hover:bg-white/10 transition-colors"
              data-testid="warroom-add-cancel"
            >
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em]">Cancel</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WarRoomTile({ symbol, symbolSet, onRemove }) {
  const bookRef = useRef({ bids: [], asks: [], midPrice: null });
  const hitsRef = useRef([]);
  const tradesHistoryRef = useRef([]);
  const [wsStatus, setWsStatus] = useState("connecting");
  const [midPrice, setMidPrice] = useState(null);
  const [snapshotErr, setSnapshotErr] = useState(null);

  const known = symbolSet.size === 0 || symbolSet.has(symbol);
  const base = symbol.replace(/(USDT|USDC|BUSD|FDUSD|BTC|ETH|BNB|EUR|TRY)$/, "");
  const quote = symbol.slice(base.length) || "USDT";

  useEffect(() => {
    let cancelled = false;
    setSnapshotErr(null);
    (async () => {
      try {
        const snap = await pairApi.snapshot(symbol);
        if (cancelled) return;
        const bids = parseLevels(snap.bids);
        const asks = parseLevels(snap.asks);
        bookRef.current = { bids, asks, midPrice: midFromBook(bids, asks) };
        setMidPrice(bookRef.current.midPrice);
      } catch (e) {
        if (!cancelled) setSnapshotErr(e?.message || "snapshot failed");
      }
    })();
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    if (!known) return undefined;
    const stream = new BinanceStream({
      symbol,
      onStatus: (s) => setWsStatus(s),
      onDepth: (d) => {
        const bids = parseLevels(d.bids);
        const asks = parseLevels(d.asks);
        const mid = midFromBook(bids, asks);
        bookRef.current = { bids, asks, midPrice: mid };
        if (mid != null) setMidPrice(mid);
      },
      onTrade: (t) => {
        const qty = parseFloat(t.q);
        const isBuyerMaker = !!t.m;
        const hist = tradesHistoryRef.current;
        hist.push(qty);
        if (hist.length > 100) hist.shift();
        let p90 = Infinity;
        if (hist.length >= 20) {
          const sorted = [...hist].sort((a, b) => a - b);
          p90 = sorted[Math.floor(sorted.length * 0.9)];
        }
        const side = isBuyerMaker ? "bid" : "ask";
        const severity = qty >= p90 ? 1 : 0;
        // War-room throttle: cap combat to ~2 kills/sec by dropping some events.
        // hitsRef is drained every frame by Battlefield3D; we bound the queue.
        if (hitsRef.current.length < 3) {
          hitsRef.current.push({ side, severity, qty });
        }
      },
    });
    stream.connect();
    return () => stream.close();
  }, [symbol, known]);

  return (
    <div
      className="relative cb-hud-chip !bg-black/50 aspect-[16/10] w-full overflow-hidden group"
      data-testid={`warroom-tile-${symbol}`}
    >
      <Link
        to={`/?pair=${symbol}`}
        className="absolute inset-0 z-30 focus:outline-none"
        aria-label={`Open ${base}/${quote} full view`}
        data-testid={`warroom-tile-link-${symbol}`}
      >
        <span className="sr-only">Open {base}/{quote}</span>
      </Link>
      <div className="absolute top-2 left-2 z-40 flex items-center gap-2 pointer-events-none">
        <span
          className="font-display text-sm font-bold tracking-tighter text-white bg-black/60 px-2 py-0.5 rounded-sm"
          data-testid={`warroom-tile-title-${symbol}`}
        >
          {base}<span className="text-slate-500 mx-0.5">/</span>{quote}
        </span>
        <span
          className="font-mono-tac text-[10px] tabular-nums text-white bg-black/60 px-2 py-0.5 rounded-sm"
          data-testid={`warroom-tile-price-${symbol}`}
        >
          {fmtPrice(midPrice)}
        </span>
      </div>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
        className="absolute top-2 right-2 z-50 cb-hud-chip p-1 hover:bg-red-500/30 transition-colors"
        aria-label={`Remove ${symbol} from war room`}
        data-testid={`warroom-tile-remove-${symbol}`}
      >
        <X className="w-3 h-3 text-white" />
      </button>
      {snapshotErr && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 pointer-events-none"
          data-testid={`warroom-tile-error-${symbol}`}
        >
          <span className="font-mono-tac text-[10px] uppercase tracking-[0.24em] text-[#FF2A2A]">
            Unreachable
          </span>
        </div>
      )}
      {wsStatus !== "open" && !snapshotErr && (
        <div
          className="absolute bottom-2 right-2 z-40 flex items-center gap-1 bg-black/60 px-2 py-0.5 rounded-sm pointer-events-none"
          data-testid={`warroom-tile-reconnecting-${symbol}`}
        >
          <Loader2 className="w-3 h-3 animate-spin text-[#FF2A2A]" />
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-300">
            {wsStatus === "connecting" ? "Connecting" : "Reconnecting"}
          </span>
        </div>
      )}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <Battlefield3D
          bookRef={bookRef}
          hitsRef={hitsRef}
          mobile={false}
          topClass="top-0"
          heightClass="h-full"
          compact
          autoOrbit={false}
          hq={false}
        />
      </div>
    </div>
  );
}
